'use client';

import { useSyncExternalStore } from 'react';
import { Button } from './ui';
import { useT } from '@/lib/locale-context';
import styles from './load-error.module.css';

/**
 * The error state in a skeleton's layout (docs/LOADING.md §2 tool 10, §4.5):
 * the shapes go, one line says what failed, then Retry and Back. ONE grammar
 * for both of its mounts: the tap's mount after 10 s without a commit
 * (`src/components/tap-mount.tsx`, Retry a document load of the target) and a
 * route's `error.tsx` (`src/app/error-screens.tsx`, Retry the boundary's
 * `retry`). The caller draws the frame around it: the peek's `Modal`, or the
 * page's own `main`.
 */
export function LoadError({
  what,
  offline,
  onRetry,
  onBack,
  testId,
}: {
  /** An item or one of its sections says "item"; every other route "page". */
  what: 'item' | 'page';
  /** The device knew it was offline when the load failed. */
  offline: boolean;
  onRetry: () => void;
  onBack: () => void;
  testId: string;
}) {
  const t = useT();
  const line = offline
    ? what === 'item'
      ? t('shell.loadError.offlineItem')
      : t('shell.loadError.offlinePage')
    : what === 'item'
      ? t('shell.loadError.item')
      : t('shell.loadError.page');
  return (
    <div className={styles.error} data-testid={testId}>
      <p className={styles.line} role="alert">
        {line}
      </p>
      <div className={styles.acts}>
        <Button variant="ghost" onClick={onRetry}>
          {t('shell.loadError.retry')}
        </Button>
        <Button variant="minimal" onClick={onBack}>
          {t('shell.loadError.back')}
        </Button>
      </div>
    </div>
  );
}

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/** Whether the device says it is offline right now (`navigator.onLine`), kept
 *  current by the online and offline events. False on the server, where no
 *  device is asked. */
export function useOffline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => !navigator.onLine,
    () => false,
  );
}
